import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Layout, Space, Spin, Typography } from 'antd';
import { LogoutOutlined, ScanOutlined } from '@ant-design/icons';
import axios from 'axios';

import BatchBuilder from './BatchBuilder';
import { clearAuth } from '../auth';
import { API_BASE } from '../App';

const { Header, Content } = Layout;
const { Text }            = Typography;

export default function ExtractionPage() {
  const navigate = useNavigate();

  const [batchItems, setBatchItems] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  function handleLogout() {
    clearAuth(); // clears sessionStorage + hard redirects to /coc_extract/login
  }

  async function handleScanAll() {
    if (batchItems.length === 0)             { setError('Please add at least one form.'); return; }
    const missingType  = batchItems.findIndex(i => !i.formType);
    if (missingType  !== -1) { setError(`Please select a form type for Form ${missingType + 1}.`); return; }
    const missingCode  = batchItems.findIndex(i => !i.testCode);
    if (missingCode  !== -1) { setError(`Please select a test code for Form ${missingCode + 1}.`); return; }
    const missingFiles = batchItems.findIndex(i => i.files.length === 0);
    if (missingFiles !== -1) { setError(`Please upload a file for Form ${missingFiles + 1}.`); return; }

    setError(null);
    setLoading(true);

    const formData = new FormData();
    batchItems.forEach((item, idx) => {
      item.files.forEach(f => formData.append(`items[${idx}].files`, f));
      formData.append(`items[${idx}].formType`,  item.formType);
      formData.append(`items[${idx}].testCode`,  item.testCode);
      formData.append(`items[${idx}].pageCount`, String(item.pageCount));
      formData.append(`items[${idx}].formLabel`, item.formLabel);
    });

    try {
      const res = await axios.post(`${API_BASE}/extract-mixed-batch`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate('/coc_result', { state: { result: res.data } });
    } catch (err) {
      const detail = err.response?.data;
      setError(typeof detail === 'string' ? detail : 'Extraction failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const totalFiles = batchItems.reduce((s, i) => s + i.files.length, 0);
  const isReady    = !loading && batchItems.length > 0;

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <Header style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: '#fff', borderBottom: '1px solid #e8e8e8',
        padding: '0 16px', height: 52,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <Text strong style={{ fontSize: 15, letterSpacing: '.1em', flex: 1 }}>DURACAN</Text>
        <Text type="secondary" style={{ fontSize: 12, letterSpacing: '.08em' }}>SCAN COC</Text>
        <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout}
          style={{ color: '#8c8c8c', marginLeft: 8 }} title="Sign out" />
      </Header>

      <Content style={{ padding: '16px 12px 40px', maxWidth: 640, margin: '0 auto', width: '100%' }}>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>

          <Card size="small" title={<Text strong>Add Forms to Batch</Text>}
            extra={<Text type="secondary" style={{ fontSize: 11 }}>Each form has its own type and test code</Text>}>
            <BatchBuilder items={batchItems} onChange={setBatchItems} apiBase={API_BASE} />
          </Card>

          {error && (
            <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />
          )}

          <Space style={{ width: '100%' }} size={10}>
            <Button size="large" style={{ flex: 1 }}
              onClick={() => { setBatchItems([]); setError(null); }} disabled={loading}>
              Clear All
            </Button>
            <Button type="primary" size="large"
              icon={loading ? <Spin size="small" /> : <ScanOutlined />}
              style={{ flex: 2 }} disabled={!isReady} onClick={handleScanAll}>
              {loading
                ? `Processing ${totalFiles} file${totalFiles !== 1 ? 's' : ''}...`
                : `Scan All${batchItems.length > 0 ? ` (${batchItems.length} form${batchItems.length !== 1 ? 's' : ''})` : ''}`
              }
            </Button>
          </Space>

        </Space>
      </Content>
    </Layout>
  );
}